import React, { useState, useEffect } from 'react';
import { Wrapper, Title, Subtitle, Content } from './styled';
import Processing from '@/presentation/components/Processing';
import Collapsible from '../../components/Collapsible';
import FileUpload from '../../../../../../../reusables/Upload';
import FormButtons from '../../components/FormButtons';
import IDHelpers from '../../../../../../../../core/helpers/IDHelper';

import {
  idOfTheStudent,
  familyId,
  recordsOfYourFamily,
  income,
  employmentSituation,
  housingStatus,
  housingServices,
  healthExpenses,
  financialState,
  generalDebts,
  educationExpenditure,
  additionalInformation
} from './components';

interface IProps {
  deleteUploadedFile: (ids: number[]) => void;
  documentsBse: any;
  downloadUploadedFile: (path: string) => void;
  handlerBack: () => void;
  isProcessing: boolean;
  isValidDocuments: boolean;
  sendDocuments: (data: any) => void;
  setIsValidDocuments: (isValid: boolean) => void;
}

const SupportingDocumentsView = ({
  deleteUploadedFile,
  documentsBse,
  downloadUploadedFile,
  handlerBack,
  isProcessing,
  isValidDocuments,
  sendDocuments,
  setIsValidDocuments
}: IProps) => {
  const [forms, setForms] = useState<any[]>([
    {
      component: idOfTheStudent,
      error: false,
      fileName: 'DNI_EST',
      files: [],
      idConcept: '1',
      isOk: false,
      show: false,
      subtitle: '(Obligatorio)',
      title: 'DNI del estudiante'
    },
    {
      component: familyId,
      error: false,
      fileName: 'DNI_FAM',
      files: [],
      idConcept: '2',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'DNI de familia'
    },
    {
      component: recordsOfYourFamily,
      error: false,
      fileName: 'ACT_FAM',
      files: [],
      idConcept: '3',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Actas de tu familia'
    },
    {
      component: income,
      error: false,
      fileName: 'INGR',
      files: [],
      idConcept: '4',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Ingresos'
    },
    {
      component: employmentSituation,
      error: false,
      fileName: 'SIT_LAB',
      files: [],
      idConcept: '5',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Situación laboral'
    },
    {
      component: housingStatus,
      error: false,
      fileName: 'EST_VIV',
      files: [],
      idConcept: '6',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Estado vivienda'
    },
    {
      component: housingServices,
      error: false,
      fileName: 'SER_VIV',
      files: [],
      idConcept: '7',
      isOk: false,
      show: false,
      subtitle: '(Obligatorio)',
      title: 'Servicios de vivienda'
    },
    {
      component: healthExpenses,
      error: false,
      fileName: 'GAST_SAL',
      files: [],
      idConcept: '8',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Gastos de salud'
    },
    {
      component: financialState,
      error: false,
      fileName: 'EST_FIN',
      files: [],
      idConcept: '9',
      isOk: false,
      show: false,
      subtitle: '(Obligatorio)',
      title: 'Estado financiero'
    },
    {
      component: generalDebts,
      error: false,
      fileName: 'DEU_GNR',
      files: [],
      idConcept: '10',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Deudas generales'
    },
    {
      component: educationExpenditure,
      error: false,
      fileName: 'GAST_EDU',
      files: [],
      idConcept: '11',
      isOk: false,
      show: false,
      subtitle: '(Según tu caso)',
      title: 'Gastos en educación'
    },
    {
      component: additionalInformation,
      error: false,
      fileName: 'INF_AD',
      files: [],
      idConcept: '12',
      isOk: false,
      show: false,
      title: 'Información adicional'
    }
  ]);

  const controllerProperty = (property: string, value: any, index: number) => {
    forms[index][property] = value;
    setForms([...forms]);
  };

  const controllerDelete = (files: any[]) => {
    const ids = files.map((file) => Number(file.id));
    deleteUploadedFile(ids);
  };

  const saveForm = (form: any) => {
    if (form.files.length > 0) {
      const tempArr: any[] = [...form.files].map(
        (file: any, index: number) => ({
          base64: file.base64,
          fileName: `${form.fileName}-${index + 1}${file.ext}`,
          idConcept: form.idConcept,
          originalFileName: file.originalName
        })
      );
      const arr = tempArr.filter((v) => v.base64);
      sendDocuments(arr);
    }
  };

  const nextForm = (index: number) => {
    if (index + 1 < forms.length) {
      controllerProperty('show', false, index);
      controllerProperty('show', true, index + 1);
    } else handlerBack();
  };

  useEffect(() => {
    forms.forEach((form, index) => {
      const arr = documentsBse.filter((v: any) => v.idType === form.idConcept);
      if (arr.length > 0) {
        controllerProperty('isOk', true, index);
        controllerProperty('files', arr, index);
      }
    });
  }, [documentsBse]);

  return (
    <Wrapper>
      <Processing isOpen={isProcessing} />
      <Title>Documentos de sustento</Title>
      <Subtitle>Adjunta uno por uno los siguientes documentos</Subtitle>
      {forms.map((form: any, index: number) => (
        <Collapsible
          key={IDHelpers.generateID()}
          index={index}
          show={form.show}
          hideIndex
          setShow={() => controllerProperty('show', !form.show, index)}
          title={form.title}
          subtitle={form.subtitle}
          error={form.error}
          isOk={form.isOk}
        >
          <Content>
            {form.component()}
            <FileUpload
              acceptedFiles={['pdf', 'ppt', 'word', 'excel', 'jpg']}
              dataFiles={form.files}
              idValue="id"
              maxFiles={10}
              maxSizeMegabytes={30}
              nameValue="originalName"
              pathValue="path"
              sizeValue="size"
              deleteFiles={(files) => {
                controllerDelete(files);
              }}
              downloadFiles={(files) => {
                downloadUploadedFile(files[0].path);
              }}
              emitFiles={(files) => {
                controllerProperty('files', files, index);
              }}
              validateDocuments={setIsValidDocuments}
            />
            <FormButtons
              handlerSend={() => saveForm(form)}
              handlerNext={() => nextForm(index)}
              formIsValid={form.files.length > 0 && isValidDocuments}
            />
          </Content>
        </Collapsible>
      ))}
    </Wrapper>
  );
};

export default SupportingDocumentsView;
